import { getUnixTime } from 'date-fns'
import jwt, { JwtPayload, verify } from 'jsonwebtoken'
import JwkToPem from 'jwk-to-pem'
import axios, { isAxiosError } from 'axios'
import { Iata } from '@reward-platform/platform-config/server'
import logger from '@reward-platform/logger/edge'
import { getAuthConfigForOpCo } from '../config'

const MEMBERSHIP_URI = 'https://avios.com/membership_id'

const WELL_KNOWN_PATH = '/.well-known/jwks.json'

/** Dedicated client for JWKS fetch; avoids global axios interceptors (and their "errorHandler" warning). */
const jwksClient = axios.create()

let partnerPemCache: Record<string, { pem: jwt.Secret; expiresAt: number } | undefined>

type AuthConfig = {
  audience: string
  issuer: string
  expireCertificateInSeconds?: number
}

const getPemCacheKey = (authConfig: AuthConfig) => `${authConfig.audience}${authConfig.issuer}`

// Expire time is only used in tests
const setPem = (authConfig: AuthConfig, newPem: string, expireTime?: number) => {
  const expireCertificateIn = authConfig.expireCertificateInSeconds || 12 * 60 * 60 // 12 hours
  const expiresAt = expireTime ?? getUnixTime(new Date()) + expireCertificateIn

  partnerPemCache = {
    ...partnerPemCache,
    [getPemCacheKey(authConfig)]: {
      pem: newPem,
      expiresAt,
    },
  }
}

const isExpiredPem = (pemToCheck: (typeof partnerPemCache)[number]) => {
  if (!pemToCheck) {
    return true
  }
  return pemToCheck.expiresAt < getUnixTime(new Date())
}

const getVerificationDetails = (authConfig: Partial<AuthConfig>) => {
  if (!authConfig?.audience || !authConfig?.issuer) {
    throw new Error('Auth0 config is missing!')
  }

  return {
    audience: authConfig.audience,
    issuer: authConfig.issuer,
    complete: false,
  }
}

const getNewPem = async (authConfig: AuthConfig) => {
  const verificationDetails = getVerificationDetails(authConfig)
  const oauthPemUrl = new URL(WELL_KNOWN_PATH, verificationDetails.issuer).toString()
  logger.debug(`Getting new PEM key from ${oauthPemUrl}`, {})

  try {
    const response = await jwksClient.get(oauthPemUrl)
    const newPem = JwkToPem(response.data.keys[0])
    logger.debug(`Successfully got PEM key ${newPem.slice(0, 5)}...${newPem.slice(-5)}`, {})
    return newPem
  } catch (err) {
    const message = isAxiosError(err)
      ? `JWKS fetch failed: ${oauthPemUrl} ${err.response?.status ?? ''} ${err.message}`
      : `JWKS fetch failed: ${oauthPemUrl} ${err instanceof Error ? err.message : String(err)}`
    logger.error(message, {
      url: oauthPemUrl,
      ...(isAxiosError(err) && {
        status: err.response?.status,
        statusText: err.response?.statusText,
      }),
      error: err instanceof Error ? { name: err.name, message: err.message } : err,
    })
    throw err
  }
}

const getPem = async (authConfig: AuthConfig) => {
  const partnerPem = partnerPemCache?.[getPemCacheKey(authConfig)]

  if (!partnerPem?.pem || isExpiredPem(partnerPem)) {
    const newPem = await getNewPem(authConfig)
    setPem(authConfig, newPem)
    return newPem
  }

  return partnerPem.pem
}

export const validateUserToken = async (opCo: Iata, accessToken?: string) => {
  if (!accessToken) {
    throw new Error('No access token provided')
  }
  const config = getAuthConfigForOpCo(opCo)
  const { audience, issuer } = getVerificationDetails({
    audience: config.audience,
    issuer: config.issuer,
  })

  const expireCertificateInSeconds = Number.isNaN(
    Number(process.env.AUTH0_EXPIRE_CERTIFICATE_IN_SECONDS)
  )
    ? Number(process.env.AUTH0_EXPIRE_CERTIFICATE_IN_SECONDS)
    : 43200 // 12 hours

  const authConfig = {
    audience,
    issuer: `https://${issuer}/`,
    expireCertificateInSeconds,
  }

  const pem = await getPem(authConfig)
  const verificationDetails = getVerificationDetails(authConfig)

  try {
    const jwtDecodedToken = verify(
      accessToken.replace('Bearer ', ''),
      pem as string,
      verificationDetails
    ) as JwtPayload
    const userId = jwtDecodedToken[MEMBERSHIP_URI]
    logger.debug(`Authorized with JWT token as ${userId}`, {})
    return accessToken
  } catch (err) {
    throw new Error('User is authorized with JWT token but the token is invalid')
  }
}
